// mf-motion — scroll motion for mf- pages
//
// Visual enhancement only — it never generates content. Without JS the page is
// already in its final state ([data-mf-anim] defaults to opacity:1 in CSS); the
// script then switches to "hide first, reveal on entry". That order matters:
// the reverse would show visitors without JS a blank page.
//
// No per-frame scrubbing on mobile: iOS coalesces scroll events during momentum
// scrolling, so a scrub always stutters. On narrow screens those effects become a
// single transition triggered on entry.

if (!window.__mfMotionLoaded) {
  window.__mfMotionLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mqDesktop = window.matchMedia('(min-width: 1024px)');
  const cleanups = [];

  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

  // ── Reveal: stagger items once the group enters the viewport ───
  function initReveal() {
    if (reduced) return;
    document.documentElement.setAttribute('data-mf-anim-ready', '');

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        // A deep link or restored scroll position can skip a group entirely;
        // it must not be left in the hidden state
        const passed = e.boundingClientRect.bottom <= 0;
        if (!e.isIntersecting && !passed) return;
        const items = e.target.querySelectorAll('[data-mf-anim]');
        items.forEach((el, i) => {
          const delay = el.getAttribute('data-mf-delay');
          el.style.transitionDelay = passed ? '0ms' : `${delay != null ? delay : i * 70}ms`;
          if (passed) el.style.transition = 'none';
        });
        e.target.setAttribute('data-mf-in', '');
        io.unobserve(e.target);
      });
    }, { threshold: 0.02, rootMargin: '0px 0px -4% 0px' });

    document.querySelectorAll('[data-mf-reveal]').forEach((g) => io.observe(g));
    cleanups.push(() => io.disconnect());
  }

  // ── Hero video ──────────────────────────────────────────────
  // The <video> carries no autoplay and preload="none" (decision 2026-09-09):
  // with autoplay the browser started pulling the 3.2 MB mp4 as soon as it was
  // parsed, competing with the poster, the fonts and the stylesheet for the first
  // second of bandwidth — on desktop and mobile alike. Playback starts from here
  // instead, once the poster has arrived (or after a 2.5 s ceiling, so a slow
  // poster never holds the video hostage), and only while the video is on screen.
  // This also handles: fade the poster once playback starts, pause off-screen to
  // save power. When play() is refused the poster simply stays — never a black
  // rectangle. Without JS the video never starts: the poster is the content, the
  // video is decoration.
  //
  // Reduced motion: a looping background film is exactly the kind of movement
  // that setting asks us to drop, so the poster stays and nothing is fetched.
  function initHeroVideo() {
    document.querySelectorAll('[data-mf-hero-video]').forEach((v) => {
      const poster = v.parentElement.querySelector('[data-mf-poster]');
      if (reduced) return;

      const reveal = () => {
        v.classList.remove('mf-opacity-0');
        if (poster) poster.classList.add('mf-opacity-0');
      };

      // armed: the poster is in (or the ceiling passed). wanted: the video is on
      // screen. Playback needs both; whichever arrives last starts it.
      let armed = false;
      let wanted = true;
      const tryPlay = () => {
        if (!armed || !wanted) return;
        v.muted = true;              // Reset every time: with an audio track mobile refuses to play
        const q = v.play();
        if (q && q.catch) q.catch(() => {});
      };
      const arm = () => {
        if (armed) return;
        armed = true;
        tryPlay();
      };
      const retry = () => tryPlay();

      // Wait for the poster's bytes, then one idle slot so its decode and the
      // first paint go first. Missing poster: arm at once.
      const posterImg = poster ? poster.querySelector('img') : null;
      const idle = (fn) => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => fn(), { timeout: 1000 });
        else window.setTimeout(fn, 200);
      };
      const ceiling = window.setTimeout(arm, 2500);
      const onPoster = () => idle(arm);
      if (!posterImg) arm();
      else if (posterImg.complete) onPoster();
      else {
        posterImg.addEventListener('load', onPoster, { once: true });
        posterImg.addEventListener('error', arm, { once: true });
      }

      v.addEventListener('playing', reveal, { once: true });
      v.addEventListener('canplay', retry);
      v.addEventListener('loadeddata', retry);
      cleanups.push(() => {
        window.clearTimeout(ceiling);
        if (posterImg) {
          posterImg.removeEventListener('load', onPoster);
          posterImg.removeEventListener('error', arm);
        }
        v.removeEventListener('playing', reveal);
        v.removeEventListener('canplay', retry);
        v.removeEventListener('loadeddata', retry);
      });

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => {
          es.forEach((e) => {
            wanted = e.isIntersecting;
            if (wanted) tryPlay();
            else v.pause();
          });
        }, { threshold: 0 });
        io.observe(v);
        cleanups.push(() => io.disconnect());
      }
    });
  }

  // ── Colour band stage ───────────────────────────────────────
  // Bands stretch left to right: each starts later and uses a larger exponent,
  // so mid-way they form a staircase and all reach the right edge together.
  // This is not "one band at a time" — that reads as a progress bar, not as the
  // staggered movement the design asks for.
  //
  // Derived from the band count instead of a fixed table. The prototype's
  // parameters were for exactly three bands ([0,0], [0.15,2.2], [0.3,3.5]);
  // a fourth or fifth band fell off the end of that table and silently reused
  // the FIRST band's timing, so extra bands moved at the first one's speed and
  // the staircase collapsed. Both formulas below reproduce the prototype
  // numbers exactly at n === 3, so the three-band page is unchanged.
  //
  // start: the last band always begins at 0.3, whatever the count — the total
  // spread stays put and the bands just pack closer together.
  // exponent: band 0 keeps the smoothstep ease (flagged by 0); the rest ramp
  // linearly from 2.2 to 3.5 across however many bands remain.
  function bandCfg(i, n) {
    const start = n > 1 ? (i * 0.3) / (n - 1) : 0;
    if (i === 0) return [start, 0];
    const exp = n > 2 ? 2.2 + (1.3 * (i - 1)) / (n - 2) : 3.5;
    return [start, exp];
  }

  function initBands() {
    const heroes = Array.from(document.querySelectorAll('[data-mf-colour-hero]'));
    if (!heroes.length) return;
    if (reduced) {
      document.querySelectorAll('[data-mf-band]').forEach((b) => { b.style.transform = 'scaleX(1)'; });
      return;
    }

    heroes.forEach((hero) => {
      const bands = Array.from(hero.querySelectorAll('[data-mf-band]'));
      const track = hero.querySelector('[data-mf-colour-track]');
      const stage = hero.querySelector('[data-mf-chero]');
      const title = hero.querySelector('[data-mf-colour-title]');
      if (!bands.length) return;

      if (!mqDesktop.matches || !track) {
        // Narrow screens have no pin distance: wipe in once on entry, staggered
        bands.forEach((b) => { b.style.transform = 'scaleX(0)'; });
        const io = new IntersectionObserver((es) => {
          es.forEach((e) => {
            if (!e.isIntersecting) return;
            // 240ms of total stagger shared across the bands, so adding a band
            // packs them tighter instead of dragging the whole wipe out.
            // At three bands this is 0/120/240ms — the previous fixed step.
            const step = bands.length > 1 ? 240 / (bands.length - 1) : 0;
            bands.forEach((b, i) => {
              b.style.transition = 'transform .8s cubic-bezier(.22,.61,.36,1) ' + Math.round(i * step) + 'ms';
              b.style.transform = 'scaleX(1)';
            });
            io.unobserve(e.target);
          });
        }, { threshold: 0.15 });
        io.observe(hero);
        cleanups.push(() => io.disconnect());
        return;
      }

      bands.forEach((b) => { b.style.transform = 'scaleX(0)'; });
      let eased = null;
      let raf = 0;

      const frame = () => {
        raf = 0;
        const vh = window.innerHeight;
        const r = track.getBoundingClientRect();
        // Pin distance = the spacer track's height: the spacer travelling from the
        // bottom of the viewport to the top is the whole animation
        const runway = Math.max(1, r.height);
        const target = clamp((vh - r.top) / runway, 0, 1);

        // Wheel events are discrete, so following the target directly steps frame by
        // frame. Track it with first-order damping instead.
        if (eased == null) eased = target;
        const gap = target - eased;
        if (Math.abs(gap) < 0.0004) eased = target;
        else { eased += gap * 0.16; schedule(); }
        const bp = eased;

        bands.forEach((b, i) => {
          const cfg = bandCfg(i, bands.length);
          const t = clamp((bp - cfg[0]) / (1 - cfg[0]), 0, 1);
          const e = cfg[1] ? Math.pow(t, cfg[1]) : t * t * (3 - 2 * t);
          b.style.transform = 'scaleX(' + e.toFixed(4) + ')';
        });

        // The backdrop turns grey only at the very end: while the bands are still
        // spreading, a white ground is what makes the grey band's edge visible
        if (stage) {
          const g = clamp((bp - 0.92) / 0.08, 0, 1);
          const c = Math.round(255 + (223 - 255) * g);
          stage.style.background = 'rgb(' + c + ',' + c + ',' + c + ')';
        }

        // The heading only shrinks, never moves: 96/96 → 64/61, always centred
        if (title) {
          const kt = clamp((bp - 0.78) / 0.22, 0, 1);
          const ke = kt * kt * (3 - 2 * kt);
          title.style.fontSize = (96 - 32 * ke).toFixed(2) + 'px';
          title.style.lineHeight = (96 - 35 * ke).toFixed(2) + 'px';
        }
      };

      const schedule = () => { if (!raf) raf = requestAnimationFrame(frame); };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      schedule();
      cleanups.push(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        if (raf) cancelAnimationFrame(raf);
        if (title) { title.style.fontSize = ''; title.style.lineHeight = ''; }
        if (stage) stage.style.background = '';
      });
    });
  }

  // ── Lineup cards on mobile ──────────────────────────────────
  // On desktop the cards are released by the intro heading's scrub (initScrub).
  // There is no scrub on mobile, so without this they keep the CSS start state
  // — opacity:0 — and the whole product lineup renders as a blank screen.
  // Measured: the mobile page showed the heading and subheading over empty white.
  function initLineupMobile() {
    if (mqDesktop.matches || reduced) return;

    const cards = Array.from(document.querySelectorAll('[data-mf-lineup-card]'));
    if (!cards.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        // Scrolled straight past (deep link, restored position): show, don't animate
        const passed = e.boundingClientRect.bottom <= 0;
        if (!e.isIntersecting && !passed) return;
        const i = cards.indexOf(e.target);
        e.target.style.transitionDelay = passed ? '0ms' : (i * 80) + 'ms';
        if (passed) e.target.style.transition = 'none';
        e.target.setAttribute('data-mf-in', '');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    cards.forEach((c) => io.observe(c));
    cleanups.push(() => io.disconnect());
  }

  // ── Lineup card image groups ────────────────────────────────
  // A group is the nth image of every card, and the whole row cross-fades to the
  // next group on one timer. Each card starts 110ms after the one before it in
  // layout order, so the change reads as a wave across the row.
  //
  // The delay comes from the markup, not from DOM order: the centre card sits
  // wherever the operator put it in the block list but always renders in the
  // middle of the row, so ordering the wave by DOM position would scramble it.
  //
  // The number of groups is the largest set on the row. A card holding fewer
  // images than that stays on its last one instead of wrapping round to its
  // first: wrapping would leave two cards sitting in different groups, which
  // reads as a glitch rather than as a row that changed together.
  //
  // Frames are rendered by Liquid and never created here — the first one carries
  // data-mf-active in the markup, so the row is complete without this script.
  //
  // Frames 2..n arrive with the hidden attribute. Without it every frame has a
  // layout box inside the lazy-load margin, and all 25 pictures (~880 KB on
  // desktop) download in the first seconds while only 5 are on show — competing
  // with the hero video for bandwidth. Revealing a frame here only lifts the
  // hidden attribute one group ahead of its turn, so its picture has a whole
  // interval to arrive; the markup itself is untouched.
  //
  // The timer runs only while the section is on screen and the tab is visible. A
  // background Chrome tab does not paint, so ticking there would spend frames on
  // transitions nobody sees and drop the visitor back onto an arbitrary group.
  function initCardRotators() {
    if (reduced) return;

    document.querySelectorAll('[data-mf-lineup-section]').forEach((section) => {
      const interval = parseInt(section.getAttribute('data-mf-rotate') || '0', 10);
      if (!interval) return;

      const sets = Array.from(section.querySelectorAll('[data-mf-card-rotator]'))
        .map((r) => ({
          frames: Array.from(r.querySelectorAll('[data-mf-card-frame]')),
          delay: parseInt(r.getAttribute('data-mf-frame-delay') || '0', 10),
        }))
        .filter((s) => s.frames.length > 1);
      if (!sets.length) return;

      const groups = sets.reduce((n, s) => Math.max(n, s.frames.length), 0);

      // Written once rather than per tick — the value never changes, and the
      // teardown below clears it
      sets.forEach((s) => s.frames.forEach((f) => { f.style.transitionDelay = s.delay + 'ms'; }));

      let step = 0;
      let timer = 0;
      let held = false;   // Theme editor holds the row still while a block is selected

      // Lift the hidden attribute from frame k so its picture starts loading.
      // Called one group ahead of the switch; a frame past the end is ignored.
      const prime = (s, k) => {
        const f = s.frames[k];
        if (f && f.hidden) f.hidden = false;
      };

      const tick = () => {
        step = (step + 1) % groups;
        sets.forEach((s) => {
          const idx = step < s.frames.length ? step : s.frames.length - 1;
          s.frames.forEach((f, i) => {
            if (i === idx) f.setAttribute('data-mf-active', '');
            else f.removeAttribute('data-mf-active');
          });
          prime(s, idx + 1);
        });
      };

      const start = () => {
        if (timer || held || document.hidden) return;
        // The first switch lands on frame 2 one interval from now — let it load
        sets.forEach((s) => prime(s, 1));
        timer = setInterval(tick, interval);
      };
      const stop = () => {
        if (!timer) return;
        clearInterval(timer);
        timer = 0;
      };

      let onScreen = false;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          onScreen = e.isIntersecting;
          if (onScreen) start();
          else stop();
        });
      }, { threshold: 0 });
      io.observe(section);

      const onVisibility = () => (document.hidden || !onScreen ? stop() : start());
      // Selecting a block means the operator is inspecting that card — the image
      // they just uploaded must not slide away while they look at it.
      const onSelect = () => { held = true; stop(); };
      const onDeselect = () => { held = false; if (onScreen) start(); };

      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('shopify:block:select', onSelect);
      document.addEventListener('shopify:block:deselect', onDeselect);

      cleanups.push(() => {
        stop();
        io.disconnect();
        sets.forEach((s) => s.frames.forEach((f) => { f.style.transitionDelay = ''; }));
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('shopify:block:select', onSelect);
        document.removeEventListener('shopify:block:deselect', onDeselect);
      });
    });
  }

  // ── Per-frame scrub: scene title / lineup heading ──
  // (Image parallax was removed by decision on 2026-08-31.)
  // Every parameter is ported from the prototype's tick(), not estimated:
  //   scene     cover=(vh-detailTop)/(vh*0.8); title opacity 1-cover*1.25, shift -70*cover
  //   lineup    p=(vh-introBottom)/(vh*0.62); heading 72→48, sub 40→20, gap 24→10
  // The lineup uses first-order damping: wheel events are discrete, and following
  // the target directly makes the font size step frame by frame.
  function initScrub() {
    if (reduced || !mqDesktop.matches) return;

    const scenes = Array.from(document.querySelectorAll('[data-mf-scene]')).map((sec) => ({
      vt: sec.querySelector('[data-mf-vtitle]'),
      cover: sec.querySelector('[data-mf-detail]'),
      ot: sec.querySelector('[data-mf-offer-title]'),
      th: sec.querySelector('[data-mf-offer-thumb]'),
      otH: 0,
    })).filter((c) => c.vt && c.cover);

    const stage = document.querySelector('[data-mf-intro-stage]');
    const group = document.querySelector('[data-mf-intro-group]');
    const title = group ? group.querySelector('h2, h3') : null;
    const sub = document.querySelector('[data-mf-intro-sub]');

    const lineupCards = Array.from(document.querySelectorAll('[data-mf-lineup-card]'));
    let lineupIn = null;
    let introE = null;
    let raf = 0;

    const frame = () => {
      raf = 0;
      const vh = window.innerHeight;

      scenes.forEach((sc) => {
        const dTop = sc.cover.getBoundingClientRect().top;
        const c = clamp((vh - dTop) / (vh * 0.8), 0, 1);
        sc.vt.style.opacity = String(clamp(1 - c * 1.25, 0, 1));
        sc.vt.style.transform = 'translate3d(0,' + (-70 * c).toFixed(1) + 'px,0)';

        // Once the panel pushes the hero image away, the left column's title and
        // thumbnail take over saying which scene this is
        if (!sc.ot) return;
        if (!sc.otH) sc.otH = sc.ot.scrollHeight;   // Measure once; re-reading would force a reflow every frame
        const t = clamp((c - 0.4) / 0.36, 0, 1);
        const e = t * t * (3 - 2 * t);              // smoothstep
        sc.ot.style.opacity = e.toFixed(3);
        sc.ot.style.height = (sc.otH * e).toFixed(1) + 'px';
        sc.ot.style.marginBottom = (20 * e).toFixed(1) + 'px';
        sc.ot.style.transform = 'translate3d(0,' + (-14 * (1 - e)).toFixed(1) + 'px,0)';
        if (sc.th) {
          const w = sc.th.clientWidth;
          sc.th.style.opacity = e.toFixed(3);
          sc.th.style.height = (w * 9 / 16 * e).toFixed(1) + 'px';
          sc.th.style.marginBottom = (20 * e).toFixed(1) + 'px';
        }
      });

      if (stage && group && title) {
        const r = stage.getBoundingClientRect();
        const p = clamp((vh - r.bottom) / (vh * 0.62), 0, 1);
        const target = 1 - Math.pow(1 - p, 3);
        if (introE == null) introE = target;
        const gap = target - introE;
        if (Math.abs(gap) < 0.0004) introE = target;
        else { introE += gap * 0.16; schedule(); }
        const e = introE;
        const dock = r.height / 2 - 122;
        group.style.transform = 'translate3d(0,' + (dock * e).toFixed(2) + 'px,0)';
        // The design changes font size rather than scaling: 72→48 for the heading and
        // 40→20 for the sub, different ratios, so a single scale cannot match it.
        // Each has to be interpolated on its own.
        const ts = (72 - 24 * e).toFixed(2);
        const ss = (40 - 20 * e).toFixed(2);
        title.style.fontSize = ts + 'px';
        title.style.lineHeight = ts + 'px';
        if (sub) {
          sub.style.fontSize = ss + 'px';
          sub.style.lineHeight = ss + 'px';
          sub.style.opacity = (1 - 0.4 * e).toFixed(3);
        }
        group.style.gap = (24 - 14 * e).toFixed(2) + 'px';

        // Cards rise one by one only after the heading has shrunk and made room;
        // threshold and delays both come from the design
        const want = p >= 0.46;
        if (want !== lineupIn) {
          lineupIn = want;
          lineupCards.forEach((el) => {
            el.style.transitionDelay = want ? (el.getAttribute('data-mf-delay') || 0) + 'ms' : '0ms';
            if (want) el.setAttribute('data-mf-in', '');
            else el.removeAttribute('data-mf-in');
          });
        }
      }
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(frame); };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
    cleanups.push(() => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
      // Clear the inline values written here when returning to narrow screens, or
      // the desktop font sizes carry over to mobile
      if (title) { title.style.fontSize = ''; title.style.lineHeight = ''; }
      if (sub) { sub.style.fontSize = ''; sub.style.lineHeight = ''; sub.style.opacity = ''; }
      if (group) { group.style.transform = ''; group.style.gap = ''; }
    });
  }

  // ── Scene hero carousel ─────────────────────────────────────
  // Always forward, never back: the wave direction is the same on every step (see
  // the loop-copy note below for how it returns to the first slide unseen).
  //
  // Reduced motion stops it entirely. An unattended carousel is exactly the kind
  // of movement that setting asks us to drop, and the first slide is rendered by
  // Liquid, so standing still is a complete picture rather than an empty frame.
  function initSlides() {
    if (reduced) return;
    const groups = Array.from(document.querySelectorAll('[data-mf-slides]'))
      .map((track) => {
        // Scene backgrounds and colour-story photos share the same rotation
        const sec = track.closest('[data-mf-scene], [data-mf-story]');
        return {
          track,
          // Scoped to this section on purpose. A bare document.querySelector
          // fallback hands a colour-story track the FIRST scene's thumbnail
          // strip, and the two then drive each other.
          //
          // Desktop only: the strip is desktop-only markup (mf-hidden lg:mf-block)
          // holding the landscape image, and cloning its first frame on a phone
          // downloads a picture nobody there will ever see. A cloned <img> has no
          // layout box, so loading="lazy" cannot hold it back and the request goes
          // out at once — unlike the Liquid-rendered original, which stays unfetched
          // inside the display:none container. Crossing 1024px reboots this module,
          // so the desktop side still gets its copy.
          thumb: mqDesktop.matches && sec ? sec.querySelector('[data-mf-thumb-track]') : null,
          n: track.children.length,
          sec,
          i: 0,
        };
      })
      .filter((g) => g.n > 1 && g.sec);
    if (!groups.length) return;

    // One direction, forever. The old cycle was a ping-pong — period 2*(n-1)
    // walked the index up and then back down, so the last slide rewound
    // through every earlier one (design 09.03 note 04 asks for this to stop).
    //
    // Going forward and then jumping home would slide backwards just as
    // visibly, so each track gets a copy of its first slide appended: moving
    // onto that copy reads as arriving back at slide 1, and once the
    // transition has run we cut to the real slide 1 with transitions off —
    // invisible, because the two frames are the same picture.
    // The copy is cloned from markup Liquid already rendered; it introduces no
    // content of its own and is hidden from screen readers.
    const appendLoopCopy = (el) => {
      if (!el || el.children.length < 2) return;
      const copy = el.firstElementChild.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-mf-loop-copy', '');
      el.appendChild(copy);
    };
    // Appended one step before it is needed — when the track moves onto its
    // last real slide — never at boot. Cloning the first slide's <img> at boot
    // fired its request at DOMContentLoaded for every scene on the page: four
    // 2560px backgrounds, ~730 KB on desktop, before the reader had scrolled at
    // all. By the time the copy is made here that picture has been on screen for
    // several seconds, so the clone is a cache hit.

    // Slides 2..n sit to the right of the clip box, where the browser's own lazy
    // loading never sees them: their fetch would begin only as the wipe starts and
    // the picture would pop in mid-transition. Flipping the next slide's images to
    // eager one step ahead gives them a whole interval to arrive. One picture per
    // tick, and only for a scene the reader is actually looking at.
    // data-mf-slide-wait is Liquid's content-visibility:hidden on slides 2..n —
    // mobile Chrome's native lazy loading ignores ancestor clipping and would
    // otherwise pull a scene's whole set down one screen early. Lifting it here
    // is the same one-step-ahead reveal as the lineup frames.
    const prime = (g, k) => {
      [g.track, g.thumb].forEach((el) => {
        const slide = el && el.children[k];
        if (!slide) return;
        slide.removeAttribute('data-mf-slide-wait');
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      });
    };
    // The first switch happens on the first tick after the scene is on screen, so
    // slide 2 has to be primed as the scene approaches, not on that tick.
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        const g = groups.find((x) => x.sec === e.target);
        if (g) prime(g, 1);
        io.unobserve(e.target);
      });
    }, { rootMargin: '50% 0px' });
    groups.forEach((g) => io.observe(g.sec));

    const shift = (el, i) => { if (el) el.style.transform = 'translate3d(' + (-i * 100) + '%,0,0)'; };
    const cut = (el) => {
      if (!el) return;
      el.style.transition = 'none';
      el.style.transform = 'translate3d(0,0,0)';
      void el.offsetWidth;          // Force the reflow, or the restored transition animates the cut
      el.style.transition = '';
    };

    // Held in a Set that each callback removes itself from: pushing to an array
    // and never clearing it grows for as long as the reader stays on the page.
    const pending = new Set();
    const timer = setInterval(() => {
      // A background tab does not paint, so the transitions would freeze mid-way
      // and the reader would come back to an arbitrary slide. Same reason the
      // lineup rotator sits out while hidden.
      if (document.hidden) return;

      groups.forEach((g) => {
        const r = g.sec.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;  // Off-screen: do nothing, save power

        g.i += 1;
        if (g.i === g.n - 1 && !g.looped) {
          g.looped = true;
          appendLoopCopy(g.track);
          appendLoopCopy(g.thumb);
        }
        shift(g.track, g.i);
        shift(g.thumb, g.i);                                     // The thumbnail follows the hero image
        prime(g, g.i + 1);

        if (g.i < g.n) return;
        // Landed on the copy — cut back to the real first slide once the
        // slide has finished. 1000ms matches mf-duration-1000 on the track.
        const id = window.setTimeout(() => {
          pending.delete(id);
          g.i = 0;
          cut(g.track);
          cut(g.thumb);
        }, 1000);
        pending.add(id);
      });
    }, 4200);

    cleanups.push(() => {
      clearInterval(timer);
      io.disconnect();
      pending.forEach(clearTimeout);
      pending.clear();
      groups.forEach((g) => {
        [g.track, g.thumb].forEach((el) => {
          if (!el) return;
          const copy = el.querySelector(':scope > [data-mf-loop-copy]');
          if (copy) copy.remove();
          el.style.transition = '';
          el.style.transform = '';
        });
      });
    });
  }

  // ── Carousel progress bar ───────────────────────────────────
  function initCarousels() {
    document.querySelectorAll('[data-mf-car-track]').forEach((track) => {
      const wrap = track.parentElement;
      const fill = wrap ? wrap.querySelector('[data-mf-car-fill]') : null;
      const prev = wrap ? wrap.querySelector('[data-mf-car-prev]') : null;
      const next = wrap ? wrap.querySelector('[data-mf-car-next]') : null;

      const update = () => {
        if (!fill) return;
        const max = track.scrollWidth - track.clientWidth;
        const bar = fill.parentElement;
        // Everything fits on screen: hide the whole track. A track with nothing to
        // scroll is a false affordance.
        if (max <= 1) { if (bar) bar.style.display = 'none'; return; }
        if (bar) bar.style.display = '';
        const ratio = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
        const p = track.scrollLeft / max;
        fill.style.width = `${(ratio * 100).toFixed(2)}%`;
        fill.style.transform = `translateX(${(((1 - ratio) / Math.max(0.0001, ratio)) * 100 * p).toFixed(2)}%)`;
      };

      // CSS cannot override a behaviour the call site names explicitly, so the
      // reduced-motion decision has to be made here too.
      const nudge = (dir) => track.scrollBy({
        left: dir * Math.round(track.clientWidth * 0.8),
        behavior: reduced ? 'auto' : 'smooth',
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if (prev) prev.addEventListener('click', () => nudge(-1));
      if (next) next.addEventListener('click', () => nudge(1));
      update();

      cleanups.push(() => {
        track.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      });

      // ── Drag the bar ──
      // The bar read as a scrollbar but did nothing, so visitors kept trying to
      // grab it. It now scrolls the track: press anywhere on it to jump there,
      // then drag. Scrolling the track still drives `update`, so the fill stays
      // in sync whichever way the row was moved.
      //
      // The hit area is the bar's box grown by HIT_PAD top and bottom. The bar
      // itself is 4px tall — reachable in theory, missed in practice — and it
      // cannot be padded in place: it is border-box with `overflow:hidden`, so
      // padding would eat the visible strip rather than extend it. Listening on
      // the parent with an inflated rect keeps the mark 4px and the target ~24px.
      // No DOM is added: the bar and its fill are both rendered by Liquid.
      const barHost = fill ? fill.parentElement : null;
      const hitHost = barHost ? barHost.parentElement : null;
      if (fill && barHost && hitHost) {
        const HIT_PAD = 10;
        let grab = -1;            // pointer offset inside the fill, -1 while idle
        let pointerId = null;

        // `hitHost` wraps the whole row, so idle moves across it are frequent and
        // must not each force a layout. Measure once and drop the cache whenever
        // the page scrolls or resizes — the only things that move the bar.
        let rect = null;
        const dropRect = () => { rect = null; };

        const inHit = (e) => {
          if (barHost.style.display === 'none') return null;
          if (!rect) rect = barHost.getBoundingClientRect();
          const r = rect;
          if (r.width <= 0) return null;
          if (e.clientY < r.top - HIT_PAD || e.clientY > r.bottom + HIT_PAD) return null;
          if (e.clientX < r.left || e.clientX > r.right) return null;
          return r;
        };

        // Map a pointer position to a scroll offset: the fill's travel is the
        // bar minus the fill's own width, and that travel maps onto the track's.
        const scrollTo = (x, r) => {
          const max = track.scrollWidth - track.clientWidth;
          const travel = r.width - fill.offsetWidth;
          if (max <= 1 || travel <= 0) return;
          track.scrollLeft = clamp((x - r.left - grab) / travel, 0, 1) * max;
        };

        const onDown = (e) => {
          if (e.button != null && e.button !== 0) return;
          const r = inHit(e);
          if (!r) return;
          e.preventDefault();                       // no text selection while dragging
          const f = fill.getBoundingClientRect();
          // On the fill: keep the point under the cursor. Beside it: centre the
          // fill on the press, which reads as "jump here".
          grab = e.clientX >= f.left && e.clientX <= f.right
            ? e.clientX - f.left
            : fill.offsetWidth / 2;
          pointerId = e.pointerId;
          if (hitHost.setPointerCapture) {
            try { hitHost.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
          }
          // .mf-track is scroll-behavior:smooth, which is right for the arrow
          // buttons and wrong here: every move would ease towards its target and
          // the bar would lag a whole animation behind the cursor. Drag is direct
          // manipulation, so take the easing off for the duration of the drag.
          track.style.scrollBehavior = 'auto';
          barHost.style.cursor = 'grabbing';
          scrollTo(e.clientX, r);
        };

        const onMove = (e) => {
          if (grab < 0) {
            // Idle: only advertise the grab when the cursor is actually over the
            // hit area, so the rest of the row keeps its own cursor.
            barHost.style.cursor = inHit(e) ? 'grab' : '';
            return;
          }
          if (pointerId != null && e.pointerId !== pointerId) return;
          if (!rect) rect = barHost.getBoundingClientRect();
          scrollTo(e.clientX, rect);
        };

        const onUp = (e) => {
          if (grab < 0) return;
          grab = -1;
          if (pointerId != null && hitHost.releasePointerCapture) {
            try { hitHost.releasePointerCapture(pointerId); } catch (err) { /* already released */ }
          }
          pointerId = null;
          track.style.scrollBehavior = '';           // back to the stylesheet's smooth
          barHost.style.cursor = inHit(e) ? 'grab' : '';
        };

        // Touch scrolls the row by swiping it; claiming the gesture here would
        // only take that away. Pointer events still fire for pen and mouse.
        barHost.style.touchAction = 'pan-x';
        hitHost.addEventListener('pointerdown', onDown);
        hitHost.addEventListener('pointermove', onMove);
        hitHost.addEventListener('pointerup', onUp);
        hitHost.addEventListener('pointercancel', onUp);
        window.addEventListener('scroll', dropRect, { passive: true });
        window.addEventListener('resize', dropRect);

        cleanups.push(() => {
          hitHost.removeEventListener('pointerdown', onDown);
          hitHost.removeEventListener('pointermove', onMove);
          hitHost.removeEventListener('pointerup', onUp);
          hitHost.removeEventListener('pointercancel', onUp);
          window.removeEventListener('scroll', dropRect);
          window.removeEventListener('resize', dropRect);
          barHost.style.cursor = '';
          barHost.style.touchAction = '';
          track.style.scrollBehavior = '';
        });
      }
    });
  }

  // ── Sticky offset ──────────────────────────────────────────
  // The scene's left column cannot use a hard-coded sticky top. The legacy
  // header collapses on scroll-down (translateY(-h)), so a fixed 152px leaves
  // a blank strip once it is gone. Track the header's live bottom edge instead
  // so the space above the column always equals the section's own top padding.
  const STICKY_GAP = 64;

  function initStickyTop() {
    if (!document.querySelector('[data-mf-offer]')) return;

    const header = Array.from(
      document.querySelectorAll('header, .t4s-section-header, [id*="section-header"]')
    ).find((el) => {
      const pos = getComputedStyle(el).position;
      return pos === 'sticky' || pos === 'fixed';
    });

    const root = document.documentElement;
    let last = -1;
    let raf = 0;

    const frame = () => {
      raf = 0;
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      const next = bottom + STICKY_GAP;
      // Only write when the rounded value actually moves: a custom property on
      // <html> invalidates style for the whole document.
      if (next === last) return;
      last = next;
      root.style.setProperty('--mf-sticky-top', next + 'px');
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(frame); };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
    cleanups.push(() => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
      root.style.removeProperty('--mf-sticky-top');
    });
  }

  // ── Header height ───────────────────────────────────────────
  // The legacy header is sticky, not fixed, so it and the announcement bar above
  // it really do take space in the flow. A 100dvh opening placed after them ends
  // up a header taller than the screen. --mf-header-h carries that height so the
  // opening can subtract it (mf-h-hero) and land on exactly one screen.
  //
  // The layout seeds this inline right after the header so the first paint is
  // already correct; this keeps it right when fonts settle or the window resizes.
  function initHeaderHeight() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const root = document.documentElement;
    let last = -1;

    const apply = () => {
      const h = Math.round(main.getBoundingClientRect().top + window.scrollY);
      if (h === last || h < 0) return;
      last = h;
      root.style.setProperty('--mf-header-h', h + 'px');
    };

    apply();
    window.addEventListener('load', apply);
    window.addEventListener('resize', apply);
    cleanups.push(() => {
      window.removeEventListener('load', apply);
      window.removeEventListener('resize', apply);
    });
  }

  function boot() {
    initHeaderHeight();
    initStickyTop();
    initLineupMobile();
    initCardRotators();
    initReveal();
    initHeroVideo();
    initBands();
    initScrub();
    initSlides();
    initCarousels();
  }

  function teardown() {
    while (cleanups.length) {
      const fn = cleanups.pop();
      try { fn(); } catch (e) { /* observer already torn down */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Re-wire after the theme editor adds or removes a section
  document.addEventListener('shopify:section:load', () => { teardown(); boot(); });
  document.addEventListener('shopify:section:unload', teardown);

  // Crossing 1024px changes which effects exist at all — the scrub and the pinned
  // colour bands are desktop-only, the lineup reveal is mobile-only. Rebuild on
  // the change so neither side is left running the other's version.
  const onBreakpoint = () => { teardown(); boot(); };
  if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onBreakpoint);
  else if (mqDesktop.addListener) mqDesktop.addListener(onBreakpoint);   // Safari < 14
}
